import { Icon, tokens } from "@vintecc/components";
import { faChevronRight } from "@vintecc/pro-light-svg-icons";
import { useRef, type SyntheticEvent } from "react";
import { type StackFrame } from "./stackTraceUtils";
import { CommonPluralTranslationKey, CommonTranslationKey, useCommonTranslation } from "@/services/culture/common.translations";

interface StackFrameItemProps {
  frame: StackFrame;
  index: number;
}

export const StackFrameItem = ({ frame, index }: StackFrameItemProps) => {
  const isFirstFrame = index === 0;
  const listItemRef = useRef<HTMLLIElement>(null);
  const { t: translateCommon } = useCommonTranslation();
  const t = (key: Exclude<CommonTranslationKey, CommonPluralTranslationKey>) => translateCommon(key);

  const handleDetailsToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    if (!event.currentTarget.open) {
      return;
    }

    // Keep expanded content visible in the scrollable stack container.
    requestAnimationFrame(() => {
      listItemRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  };

  if (!frame.file) {
    return (
      <li
        ref={listItemRef}
        className={`py-2 px-3 text-sm font-mono leading-relaxed border-b last:border-b-0 border-gray-200 ${
          isFirstFrame ? "bg-red-50 border-l-4 border-red-400 text-red-900" : "text-gray-700"
        }`}
      >
        {frame.raw}
      </li>
    );
  }

  const fileName = frame.file.split("/").pop() || frame.file;

  return (
    <li ref={listItemRef} className={`border-b last:border-b-0 border-gray-200 ${isFirstFrame ? "bg-red-50 border-l-4 border-red-400" : ""}`}>
      <details className="group" open={isFirstFrame} onToggle={handleDetailsToggle}>
        <summary className="list-none cursor-pointer py-3 px-3 flex items-start gap-2 hover:bg-gray-50 transition-colors">
          <Icon
            icon={faChevronRight}
            size="sm"
            color={isFirstFrame ? tokens.color.red[400] : tokens.color.grey[400]}
            className="shrink-0 mt-0.5 transition-transform duration-200 group-open:rotate-90"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-mono ${isFirstFrame ? "text-red-700 font-semibold" : "text-blue-600"}`}>{frame.function}</span>
              <span className={`font-mono text-xs ${isFirstFrame ? "text-red-600" : "text-gray-500"}`}>{fileName}</span>
              {frame.line && (
                <span className={`font-mono text-xs ${isFirstFrame ? "text-red-500" : "text-gray-400"}`}>
                  {frame.line}:{frame.column || 0}
                </span>
              )}
              {isFirstFrame && <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-1 rounded">{t("errors.sourceFrame")}</span>}
            </div>
          </div>
        </summary>
        <div className="bg-gray-50 border-t border-gray-200 px-3 py-2 text-xs font-mono text-gray-600 break-all">
          <div className="text-gray-500 mb-1">
            {frame.file}:{frame.line}:{frame.column || 0}
          </div>
        </div>
      </details>
    </li>
  );
};
